import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileSpreadsheet, Loader2 } from 'lucide-react';
import { API_BASE_URL } from './config/api';
import { saveBlob } from '../utils/saveBlob';
import { athensYmd, isYmd } from '../utils/athensDate';
import { useToast } from './shared/Toast';
import { GHOST_BTN, PRIMARY_BTN } from './shared/listPage';
import './shared/listPage/listPage.css';

/**
 * Ταμείο → «Παραστατικά» (διαχείριση καταστήματος) → «Εξαγωγή σε Excel» (27/10/2026): ΟΛΑ τα παραστατικά μιας
 * περιόδου, όχι μόνο τα 100 του πίνακα. Ο server φτιάχνει το ΙΔΙΟ .xlsx με τα κεντρικά
 * (GET …/fiscal/documents/export.xlsx → services/orgFiscalDocuments: ίδιες στήλες, πιστωτικά αρνητικά, ώρα
 * Ελλάδας) και αρνείται πάνω από 50.000 με το πλήθος. Περίοδος σε μέρες Ελλάδας· προεπιλογή ο τρέχων μήνας,
 * ένα πάτημα για τον προηγούμενο (ο λογιστής θέλει συνήθως μήνα). Ο τύπος ξεκινά από το φίλτρο της σελίδας.
 */

export interface FiscalExportTypeOption {
  value: string;
  label: string;
}

export interface FiscalExcelExportProps {
  storeId: number;
  /** Το φίλτρο τύπου της σελίδας ('all' ή κωδικός ΑΑΔΕ): από εκεί ξεκινά ο τύπος της εξαγωγής. */
  typeFilter: string;
  typeOptions: FiscalExportTypeOption[];
  allTypesLabel: string;
}

type Period = { from: string; to: string };
type MonthPreset = 'thisMonth' | 'lastMonth';

const ymdOf = (d: Date): string => d.toISOString().slice(0, 10);

/** Τρέχων μήνας = από την 1η ως σήμερα· προηγούμενος = ολόκληρος (και Δεκέμβριος όταν είμαστε Ιανουάριο). */
export function monthPeriod(preset: MonthPreset, today: string): Period {
  const [y, m] = today.split('-').map(Number);
  if (preset === 'thisMonth') return { from: `${today.slice(0, 8)}01`, to: today };
  // Μεσημέρι UTC: η αλλαγή ώρας δεν μετακινεί ποτέ τη μέρα.
  return { from: ymdOf(new Date(Date.UTC(y, m - 2, 1, 12))), to: ymdOf(new Date(Date.UTC(y, m - 1, 0, 12))) };
}

/** Όνομα αρχείου από το Content-Disposition (αν το εκθέτει ο server — όχι πάντα σε άλλο origin), αλλιώς το δοσμένο. */
const fileNameOf = (disposition: string | null, fallback: string): string => {
  const m = /filename="?([^";]+)"?/i.exec(disposition || '');
  return m ? m[1] : fallback;
};

const FiscalExcelExport: React.FC<FiscalExcelExportProps> = ({ storeId, typeFilter, typeOptions, allTypesLabel }) => {
  const { t, i18n } = useTranslation(['fiscalDocuments']);
  const { showToast } = useToast();
  const uid = useId();
  const today = athensYmd();
  const [open, setOpen] = useState(false);
  const [period, setPeriod] = useState<Period>(() => monthPeriod('thisMonth', today));
  const [type, setType] = useState(typeFilter);
  const [working, setWorking] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const fromRef = useRef<HTMLInputElement>(null);

  // Κλικ έξω κλείνει το παράθυρο (όπως το DateRange)· όχι όσο κατεβαίνει το αρχείο.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (!working && boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    fromRef.current?.focus();
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, working]);

  const toggle = () => {
    if (!open) setType(typeFilter);
    setOpen(o => !o);
  };

  const close = () => {
    if (working) return;
    setOpen(false);
    buttonRef.current?.focus();
  };

  const okDay = (d: string) => isYmd(d) && d <= today;
  const valid = okDay(period.from) && okDay(period.to) && period.from <= period.to;
  const presetOf = (['thisMonth', 'lastMonth'] as MonthPreset[])
    .find(p => { const r = monthPeriod(p, today); return r.from === period.from && r.to === period.to; });

  const download = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid || working) return;
    setWorking(true);
    try {
      const qs = new URLSearchParams({ from: period.from, to: period.to });
      if (type && type !== 'all') qs.set('type', type);
      const response = await fetch(`${API_BASE_URL}/api/stores/${storeId}/fiscal/documents/export.xlsx?${qs}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` },
      });
      if (!response.ok) {
        let data: any = null;
        try { data = await response.json(); } catch { /* μένει το γενικό μήνυμα */ }
        const nf = new Intl.NumberFormat(i18n?.language === 'en' ? 'en-GB' : 'el-GR');
        let msg = t('fiscalDocuments:export.failed');
        if (data?.code === 'too_many_documents') {
          msg = t('fiscalDocuments:export.tooMany', { total: nf.format(Number(data.count) || 0), limit: nf.format(Number(data.limit) || 0) });
        } else if (data?.code === 'no_documents') {
          msg = t('fiscalDocuments:export.empty');
        } else if (response.status === 403) {
          msg = t('fiscalDocuments:export.forbidden');
        }
        showToast('error', msg);
        return;
      }
      const blob = await response.blob();
      saveBlob(blob, fileNameOf(response.headers.get('content-disposition'), `parastatika_kat${storeId}_${period.from}_${period.to}.xlsx`));
      setOpen(false);
    } catch (error) {
      console.error('Fiscal export failed:', error);
      showToast('error', t('fiscalDocuments:export.failed'));
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="lp-period" ref={boxRef} data-testid="fiscal-export">
      <button
        ref={buttonRef}
        type="button"
        className={GHOST_BTN}
        onClick={toggle}
        aria-expanded={open}
        aria-controls={open ? `${uid}-export` : undefined}
        title={t('fiscalDocuments:export.hint')}
        data-testid="fiscal-export-open"
      >
        <FileSpreadsheet className="w-4 h-4" aria-hidden="true" />
        {t('fiscalDocuments:export.button')}
      </button>
      {open && (
        <form
          id={`${uid}-export`}
          className="lp-pop lp-pop-end"
          role="dialog"
          aria-label={t('fiscalDocuments:export.title')}
          aria-busy={working || undefined}
          onSubmit={download}
          onKeyDown={e => {
            if (e.key !== 'Escape') return;
            e.preventDefault();
            e.stopPropagation();
            close();
          }}
        >
          <p className="lp-pop-title">{t('fiscalDocuments:export.title')}</p>
          <div className="lp-seg" role="radiogroup" aria-label={t('fiscalDocuments:export.period')}>
            {(['thisMonth', 'lastMonth'] as MonthPreset[]).map(p => (
              <button key={p} type="button" role="radio" aria-checked={presetOf === p}
                onClick={() => setPeriod(monthPeriod(p, today))} data-testid={`fiscal-export-${p}`}>
                {t(`fiscalDocuments:export.${p}`)}
              </button>
            ))}
          </div>
          <div className="lp-pop-row mt-3">
            <label className="lp-pop-field">
              <span>{t('fiscalDocuments:export.from')}</span>
              <input ref={fromRef} type="date" className="lp-date" value={period.from} max={period.to || today}
                onChange={e => setPeriod(p => ({ ...p, from: e.target.value }))} data-testid="fiscal-export-from" />
            </label>
            <label className="lp-pop-field">
              <span>{t('fiscalDocuments:export.to')}</span>
              <input type="date" className="lp-date" value={period.to} min={period.from || undefined} max={today}
                onChange={e => setPeriod(p => ({ ...p, to: e.target.value }))} data-testid="fiscal-export-to" />
            </label>
          </div>
          <label className="lp-pop-field mt-3">
            <span>{t('fiscalDocuments:typeFilter')}</span>
            <select className="lp-select" value={type} onChange={e => setType(e.target.value)} data-testid="fiscal-export-type">
              <option value="all">{allTypesLabel}</option>
              {typeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          {!valid && <p className="lp-pop-error" role="alert">{t('fiscalDocuments:export.invalid')}</p>}
          <p className="lp-pop-hint">{t('fiscalDocuments:export.hint')}</p>
          <div className="lp-pop-actions">
            <button type="button" className={GHOST_BTN} onClick={close} disabled={working}>{t('fiscalDocuments:export.cancel')}</button>
            <button type="submit" className={PRIMARY_BTN} disabled={!valid || working} data-testid="fiscal-export-download">
              {working
                ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                : <FileSpreadsheet className="w-4 h-4" aria-hidden="true" />}
              {working ? t('fiscalDocuments:export.working') : t('fiscalDocuments:export.download')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default FiscalExcelExport;
